/*
 * Copyright 2020 The Backstage Authors
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */

export type Transformer = (dom: Element) => Element | Promise<Element>;

const cleanRawHtmlString = (html: string): string => {
  const nonce =
    (window as any).cspNonce ||
    document
      .querySelector('meta[property="csp-nonce"]')
      ?.getAttribute('content');

  // create an array to hold the generated CSS rules and a counter for class names
  const inlineStyles: string[] = [];
  let styleCounter = 0;

  // Extract inline styles and replace them with classes
  let cleanedHtml = html.replace(/\s+style="([^"]*)"/gi, (_, styleContent) => {
    const className = `inline-style-${styleCounter++}`;
    inlineStyles.push(`.${className} { ${styleContent} }`);
    return ` class="${className}"`;
  });

  // If there are styles to apply, create a <style> tag with nonce
  if (inlineStyles.length > 0 && nonce) {
    const styleTag = `<style nonce="${nonce}">${inlineStyles.join(
      '\n',
    )}</style>`;
    // Insert the style tag at the beginning of the document
    cleanedHtml = styleTag + cleanedHtml;
  }

  // Add nonce to inline style
  if (nonce) {
    cleanedHtml = cleanedHtml.replace(
      /<style(?![^>]*nonce=)([^>]*)>/gi,
      `<style$1 nonce="${nonce}">`,
    );
  }

  return cleanedHtml;
};

export const transform = async (
  html: string | Element,
  transformers: Transformer[],
): Promise<Element> => {
  let dom: Element;
  if (typeof html === 'string') {
    const cleanedHtml = cleanRawHtmlString(html);

    dom = new DOMParser().parseFromString(
      cleanedHtml,
      'text/html',
    ).documentElement;
  } else if (html instanceof Element) {
    dom = html;
  } else {
    throw new Error('dom is not a recognized type');
  }

  for (const transformer of transformers) {
    dom = await transformer(dom);
  }

  return dom;
};
